import { RevealPair, type PairAnswer } from './RevealPair';
import './RevealCard.css';

/* One revealed question as a bounded card (card XRKkcYceOxq9), now the single
   implementation for every surface that shows a past reveal — Reveal history
   and the inline list on Us > Questions (founder email 2026-10-27: "the initial
   reveal history attached should have same implementation as the full reveal").
   Before this the inline list rendered its own stacked variant, so the same
   pair of answers read differently depending on where you looked. */

export interface RevealCardQuestion {
  questionId: string;
  /** YYYY-MM-DD in the couple's day — one question per day. */
  dateKey: string;
  prompt: string;
  /** Engine theme, the category chip. Absent on rows written before the
   *  engine, which fall back to "Uncategorised". */
  theme?: string;
}

export interface RevealCardData {
  question: RevealCardQuestion;
  answers: PairAnswer[];
}

/** dateKey is already the couple's own day — format from its parts so the
 *  date cannot shift a day across timezones. */
export function dayLabel(dateKey: string): string {
  const [y, m, d] = dateKey.split('-').map(Number);
  if (!y || !m || !d) return dateKey;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/** The 11 seed themes are lowercase slugs; the chip shows the word, not the
 *  slug. Anything unrecognised is Uncategorised rather than a made-up label. */
const THEME_LABELS: Record<string, string> = {
  money: 'Money',
  intimacy: 'Intimacy',
  family: 'Family',
  future: 'Future',
  fun: 'Fun',
  stress: 'Stress',
  memories: 'Memories',
  values: 'Values',
  routines: 'Routines',
  gratitude: 'Gratitude',
  adventure: 'Adventure',
};

export function themeLabel(theme?: string): string {
  if (!theme) return 'Uncategorised';
  return THEME_LABELS[theme] ?? 'Uncategorised';
}

interface RevealCardProps {
  reveal: RevealCardData;
  /** Column order, normally the couple's member order. */
  order: string[];
  nameFor: (memberId: string) => string;
  /** Copy for a reveal nobody answered. */
  emptyText?: string;
  testId?: string;
}

export function RevealCard({ reveal, order, nameFor, emptyText = 'No answers arrived before this reveal opened.', testId }: RevealCardProps) {
  const { question, answers } = reveal;
  return (
    <article className="rc-card" data-theme={question.theme ?? ''} data-reveal-card={testId ?? ''}>
      <div className="rc-head">
        <span className="rc-chip">{themeLabel(question.theme)}</span>
        <span className="rc-date">{dayLabel(question.dateKey)}</span>
      </div>
      <h3 className="rc-prompt">{question.prompt}</h3>
      {answers.length === 0 ? (
        <p className="rc-empty">
          {/* Sol review 2026-10-17: this line promised "Either of you can still
              add one" while nothing here offers that action — the composer
              lives on the current question. Say what happened and nothing
              more. */}
          {emptyText}
        </p>
      ) : (
        <RevealPair answers={answers} order={order} nameFor={nameFor} testId={testId} />
      )}
    </article>
  );
}
